import { useFieldArray, type UseFormReturn, useWatch } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, Copy, ArrowUp, ArrowDown } from 'lucide-react';
import { FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form';
import { type EventFormValues } from '@/lib/schemas/event';
import { motion, AnimatePresence } from 'framer-motion';
import { type FormField as FormFieldType } from '@/types';

interface CustomFormBuilderProps {
  form: UseFormReturn<EventFormValues>;
  nestIndex: number;
}

export function CustomFormBuilder({ form, nestIndex }: CustomFormBuilderProps) {
  const { fields, append, remove, move, insert } = useFieldArray({
    control: form.control,
    name: `tickets.${nestIndex}.formSchema`,
  });

  const formSchema = useWatch({
    control: form.control,
    name: `tickets.${nestIndex}.formSchema`,
  });

  const addOption = (fieldIndex: number) => {
    const currentOptions =
      form.getValues(`tickets.${nestIndex}.formSchema.${fieldIndex}.options`) || [];
    form.setValue(`tickets.${nestIndex}.formSchema.${fieldIndex}.options`, [
      ...currentOptions,
      `Option ${currentOptions.length + 1}`,
    ]);
  };

  const removeOption = (fieldIndex: number, optionIndex: number) => {
    const currentOptions =
      form.getValues(`tickets.${nestIndex}.formSchema.${fieldIndex}.options`) || [];
    const newOptions = [...currentOptions];
    newOptions.splice(optionIndex, 1);
    form.setValue(`tickets.${nestIndex}.formSchema.${fieldIndex}.options`, newOptions);
  };

  const handlePasteOption = (
    fieldIndex: number,
    optionIndex: number,
    e: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const text = e.clipboardData.getData('text');
    if (text.includes('\n') || text.includes('\r')) {
      e.preventDefault();
      const pastedLines = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

      if (pastedLines.length > 0) {
        const currentOptions =
          form.getValues(`tickets.${nestIndex}.formSchema.${fieldIndex}.options`) || [];
        const newOptions = [...currentOptions];
        newOptions[optionIndex] = pastedLines[0];
        newOptions.splice(optionIndex + 1, 0, ...pastedLines.slice(1));
        form.setValue(`tickets.${nestIndex}.formSchema.${fieldIndex}.options`, newOptions);
      }
    }
  };

  const duplicateField = (index: number) => {
    const fieldToDuplicate = form.getValues(`tickets.${nestIndex}.formSchema.${index}`);
    insert(index + 1, {
      ...fieldToDuplicate,
      id: crypto.randomUUID(),
    } as FormFieldType);
  };

  return (
    <div className="space-y-4">
      <AnimatePresence mode="popLayout">
        {fields.map((field, index) => {
          const fieldType = formSchema?.[index]?.type;

          return (
            <motion.div
              key={field.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-accent/5 rounded-xl border border-border p-4 space-y-4 group relative"
            >
              <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                  onClick={() => move(index, index - 1)}
                  disabled={index === 0}
                  type="button"
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                  onClick={() => move(index, index + 1)}
                  disabled={index === fields.length - 1}
                  type="button"
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-primary hover:bg-primary/10"
                  onClick={() => duplicateField(index)}
                  type="button"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-destructive hover:bg-destructive/10"
                  onClick={() => remove(index)}
                  type="button"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-[2fr,1fr]">
                <FormField
                  control={form.control}
                  name={`tickets.${nestIndex}.formSchema.${index}.label`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">Question Label</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. What is your T-shirt size?" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`tickets.${nestIndex}.formSchema.${index}.type`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">Field Type</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="TEXT">Text Input</SelectItem>
                          <SelectItem value="TEXTAREA">Long Text</SelectItem>
                          <SelectItem value="NUMBER">Number</SelectItem>
                          <SelectItem value="SELECT">Dropdown</SelectItem>
                          <SelectItem value="MULTI_SELECT">Multi-choice</SelectItem>
                          <SelectItem value="IMAGE">Image Upload</SelectItem>
                          <SelectItem value="DATE">Date Picker</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex items-center gap-6">
                <FormField
                  control={form.control}
                  name={`tickets.${nestIndex}.formSchema.${index}.required`}
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <Label className="text-sm text-muted-foreground font-medium cursor-pointer">
                        Required
                      </Label>
                    </FormItem>
                  )}
                />

                {fieldType === 'IMAGE' && (
                  <FormField
                    control={form.control}
                    name={`tickets.${nestIndex}.formSchema.${index}.enableCrop`}
                    render={({ field }) => (
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <Switch checked={field.value ?? true} onCheckedChange={field.onChange} />
                        </FormControl>
                        <Label className="text-sm text-muted-foreground font-medium cursor-pointer">
                          Enable Cropping
                        </Label>
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {fieldType === 'IMAGE' && formSchema?.[index]?.enableCrop !== false && (
                <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Crop Aspect Ratio
                  </Label>
                  <FormField
                    control={form.control}
                    name={`tickets.${nestIndex}.formSchema.${index}.cropAspect`}
                    render={({ field }) => (
                      <div className="space-y-3">
                        <Select
                          value={
                            field.value === 1
                              ? '1:1'
                              : field.value === 4 / 3
                                ? '4:3'
                                : field.value === 16 / 9
                                  ? '16:9'
                                  : field.value === 3 / 2
                                    ? '3:2'
                                    : field.value
                                      ? 'custom'
                                      : '1:1'
                          }
                          onValueChange={(val) => {
                            const aspectMap: Record<string, number> = {
                              '1:1': 1,
                              '4:3': 4 / 3,
                              '16:9': 16 / 9,
                              '3:2': 3 / 2,
                              custom: 1.51,
                            };
                            field.onChange(aspectMap[val]);
                          }}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1:1">1:1 (Square)</SelectItem>
                            <SelectItem value="4:3">4:3 (Standard)</SelectItem>
                            <SelectItem value="16:9">16:9 (Widescreen)</SelectItem>
                            <SelectItem value="3:2">3:2 (Photo)</SelectItem>
                            <SelectItem value="custom">Custom</SelectItem>
                          </SelectContent>
                        </Select>
                        {field.value && ![1, 4 / 3, 16 / 9, 3 / 2].includes(field.value) && (
                          <div className="flex gap-2 items-center">
                            <Input
                              type="number"
                              placeholder="Aspect Ratio"
                              className="h-8 text-sm w-32"
                              step="0.01"
                              value={field.value}
                              onChange={(e) => field.onChange(parseFloat(e.target.value) || 1)}
                            />
                            <span className="text-xs text-muted-foreground">
                              Width/Height Ratio
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              )}

              {(fieldType === 'SELECT' || fieldType === 'MULTI_SELECT') && (
                <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
                    Dropdown Options
                  </Label>
                  <div className="space-y-2">
                    {(formSchema?.[index]?.options || []).map((_, optIndex) => (
                      <div key={optIndex} className="flex gap-2">
                        <FormField
                          control={form.control}
                          name={`tickets.${nestIndex}.formSchema.${index}.options.${optIndex}`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <Input
                                  {...field}
                                  className="h-8 text-sm"
                                  placeholder={`Option ${optIndex + 1} (or paste multi-line list)`}
                                  onPaste={(e) => handlePasteOption(index, optIndex, e)}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          type="button"
                          onClick={() => removeOption(index, optIndex)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      size="sm"
                      variant="ghost"
                      type="button"
                      onClick={() => addOption(index)}
                      className="text-primary hover:text-primary/80 h-8 px-2"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add Option
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full border-dashed py-6 text-muted-foreground hover:text-primary hover:border-primary/50"
        onClick={() =>
          append({
            id: crypto.randomUUID(),
            label: '',
            type: 'TEXT',
            required: true,
            options: [],
          } as FormFieldType)
        }
      >
        <Plus className="mr-2 h-4 w-4" /> Add Question
      </Button>
    </div>
  );
}
